/*
 * Zoey & Lex bag and product page behavior. Loaded with defer by
 * sections/cart-drawer.liquid, which layout/theme.liquid renders on every page.
 *
 * Bag (the live site's BagDrawer.astro, on Shopify's Ajax Cart API):
 *  - Any [data-bag-open] link (the header Bag) opens the drawer instead of going to /cart.
 *  - [data-qty] buttons inside a [data-line-key] line change that line (0 removes it).
 *  - [data-bag-note] boxes save the cart note when they lose focus; the gift tag
 *    preview ([data-note-preview]) follows the typing.
 *  - Any button with data-add-variant="<variant id>" adds one of that variant.
 *  - After every change, each [data-cart-root] on the page (the drawer, the /cart page)
 *    is re-rendered with the Section Rendering API: its [data-bag-region] parts are
 *    swapped in place, and `cart:updated` fires with { item_count } so
 *    assets/theme.js updates every [data-cart-count].
 *  - window.ZLCart = { add, change, open, close, refresh } for other sections.
 *
 * Product page (<zl-product>, sections/main-product.liquid):
 *  - Option buttons pick the variant, update the price, sale price, Add to Bag
 *    button, the ?variant= URL and, when the variant has its own photo, the main photo.
 *  - Small photos swap the main photo.
 *  - Quantity steps 1 to 20.
 *  - Add to Bag posts the form (with personalization) to /cart/add.js, then opens the bag.
 */
(() => {
  if (window.ZLCart) return;

  const drawer = () => document.getElementById('bag');
  /** Words set in the theme editor (Bag drawer > Short messages), with the live wording as fallback. */
  const say = (key, fallback) => drawer()?.dataset[key] || fallback;
  const roots = () => [...document.querySelectorAll('[data-cart-root][data-section-id]')];
  const sectionIds = () => roots().map((r) => r.dataset.sectionId);
  const currency = () => (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';

  /** Whole dollars ($29) unless the price has cents ($27.50), like the live site. */
  const money = (cents) => {
    const n = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency(),
      minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(n);
  };

  /* ---------- Cart requests ---------- */

  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  async function request(path, body) {
    const ids = sectionIds();
    let init;
    if (body instanceof FormData) {
      if (ids.length) {
        body.append('sections', ids.join(','));
        body.append('sections_url', location.pathname);
      }
      init = { method: 'POST', headers: { Accept: 'application/json' }, body };
    } else {
      const payload = ids.length ? { ...body, sections: ids, sections_url: location.pathname } : body;
      init = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      };
    }
    const res = await fetch(`${root()}${path}`, init);
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.status) {
      throw new Error(json.description || json.message || say('textFailed', "We couldn't update your bag. Please try again."));
    }
    if (json.sections) render(json.sections);
    return json;
  }

  async function refresh() {
    const ids = sectionIds();
    if (!ids.length) return;
    const res = await fetch(`${location.pathname}?sections=${ids.join(',')}`, { headers: { Accept: 'application/json' } });
    if (res.ok) render(await res.json());
  }

  const add = async (body) => {
    const json = await request('cart/add.js', body);
    open();
    return json;
  };
  const change = (key, quantity) => request('cart/change.js', { id: key, quantity });
  const setNote = (note) => request('cart/update.js', { note });

  /* ---------- Section Rendering ---------- */

  function render(sections) {
    let count = null;
    for (const [id, html] of Object.entries(sections)) {
      if (!html) continue;
      const current = document.querySelector(`[data-cart-root][data-section-id="${CSS.escape(id)}"]`);
      if (!current) continue;
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-root]');
      if (!next) continue;
      current.dataset.itemCount = next.dataset.itemCount;
      count = Number(next.dataset.itemCount);
      next.querySelectorAll('[data-bag-region]').forEach((fresh) => {
        const old = current.querySelector(`[data-bag-region="${fresh.dataset.bagRegion}"]`);
        if (!old) return;
        // Keep whatever box the shopper is typing in (the gift note) exactly as it is.
        const active = document.activeElement;
        const keep = active && old.contains(active) && active.id ? active : null;
        const caret = keep && 'selectionStart' in keep ? [keep.selectionStart, keep.selectionEnd] : null;
        const typed = keep ? keep.value : null;
        old.replaceWith(fresh);
        if (keep) {
          const twin = document.getElementById(keep.id);
          if (twin) {
            twin.value = typed;
            twin.focus();
            if (caret) twin.setSelectionRange(caret[0], caret[1]);
            syncNotePreview(twin);
          }
        }
      });
    }
    if (count !== null) {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item_count: count } }));
    }
  }

  /* ---------- Drawer ---------- */

  let lastFocus = null;
  const isOpen = () => drawer()?.classList.contains('is-open');

  function open() {
    const el = drawer();
    if (!el) return false;
    if (!isOpen()) lastFocus = document.activeElement;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    setTimeout(() => el.querySelector('button[data-bag-close]')?.focus(), 60);
    return true;
  }

  function close() {
    const el = drawer();
    if (!el || !isOpen()) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    if (lastFocus instanceof HTMLElement) lastFocus.focus();
  }

  function fail(scope, message) {
    const el = (scope && scope.closest('[data-cart-root]')?.querySelector('[data-bag-error]')) ||
      drawer()?.querySelector('[data-bag-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(el._timer);
    el._timer = setTimeout(() => (el.hidden = true), 6000);
  }

  document.addEventListener('click', async (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;

    const opener = t.closest('[data-bag-open]');
    if (opener && drawer()) {
      e.preventDefault();
      open();
      return;
    }
    if (t.closest('[data-bag-close]')) {
      close();
      return;
    }

    const qty = t.closest('[data-qty]');
    const line = qty && qty.closest('[data-line-key]');
    if (qty && line) {
      line.style.opacity = '0.5';
      try {
        await change(line.dataset.lineKey, Math.max(0, Number(qty.dataset.qty)));
      } catch (err) {
        line.style.opacity = '';
        fail(line, err.message);
      }
      return;
    }

    const quick = t.closest('[data-add-variant]');
    if (quick && !quick.disabled) {
      e.preventDefault();
      const label = quick.textContent;
      quick.disabled = true;
      quick.textContent = say('textAdding', 'Adding…');
      try {
        await add({ items: [{ id: Number(quick.dataset.addVariant), quantity: 1 }] });
      } catch (err) {
        fail(null, err.message);
        open();
      }
      quick.disabled = false;
      quick.textContent = label;
    }
  });

  document.addEventListener('keydown', (e) => {
    if (!isOpen()) return;
    if (e.key === 'Escape') {
      close();
      return;
    }
    // Keep Tab inside the open drawer.
    if (e.key === 'Tab') {
      const panel = drawer().querySelector('.bag__panel');
      const items = [...panel.querySelectorAll('a[href], button:not([disabled]), textarea, input, [tabindex]:not([tabindex="-1"])')]
        .filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      } else if (!panel.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  /* ---------- Gift note ---------- */

  function syncNotePreview(box) {
    const preview = box.closest('.bag-note')?.querySelector('[data-note-preview]');
    if (preview) preview.textContent = box.value.trim() || preview.dataset.empty || '';
  }

  document.addEventListener('input', (e) => {
    const box = e.target;
    if (!(box instanceof HTMLTextAreaElement) || !box.matches('[data-bag-note]')) return;
    syncNotePreview(box);
    const status = box.closest('.bag-note')?.querySelector('[data-note-status]');
    if (status) status.textContent = '';
  });

  document.addEventListener('change', async (e) => {
    const box = e.target;
    if (!(box instanceof HTMLTextAreaElement) || !box.matches('[data-bag-note]')) return;
    const status = () => document.getElementById(box.id)?.closest('.bag-note')?.querySelector('[data-note-status]');
    const show = (text) => { const s = status(); if (s) s.textContent = text; };
    show(say('textNoteSaving', 'Saving…'));
    try {
      await setNote(box.value.trim());
      show(say('textNoteSaved', 'Note saved.'));
    } catch {
      show(say('textNoteFailed', "Your note didn't save. Please try again."));
    }
  });

  // Coming back with the Back button can show an old bag; re-read it.
  addEventListener('pageshow', (e) => {
    if (e.persisted) refresh().catch(() => {});
  });

  window.ZLCart = { add, change, open, close, refresh, money };

  /* ---------- Product page ---------- */

  class ZlProduct extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      const data = this.querySelector('script[data-variants]');
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.form = document.getElementById(this.dataset.formId);
      this.main = this.querySelector('[data-main-photo]');
      this.thumbs = [...this.querySelectorAll('[data-photo]')];

      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.showPhoto(i)));
      this.addEventListener('change', (e) => {
        if (e.target instanceof HTMLInputElement && e.target.type === 'radio') this.sync(true);
      });

      if (!this.form) return;
      this.qty = this.form.querySelector('input[name=quantity]');
      this.button = this.form.querySelector('[data-add]');
      this.error = this.form.querySelector('[data-add-error]');
      this.form.querySelectorAll('[data-step]').forEach((b) =>
        b.addEventListener('click', () => {
          if (!this.qty) return;
          const next = (Number(this.qty.value) || 1) + Number(b.dataset.step);
          this.qty.value = String(Math.min(20, Math.max(1, next)));
        }),
      );
      this.form.addEventListener('submit', (e) => this.submit(e));
    }

    get fieldsets() {
      return [...this.querySelectorAll('[data-option-index]')];
    }

    showPhoto(i) {
      const t = this.thumbs[i];
      if (!this.main || !t) return;
      this.main.srcset = t.dataset.srcset || '';
      this.main.src = t.dataset.src;
      this.main.alt = t.dataset.alt || '';
      this.thumbs.forEach((b, j) => b.setAttribute('aria-pressed', String(j === i)));
    }

    current() {
      const chosen = this.fieldsets.map((fs) => fs.querySelector('input:checked')?.value);
      if (!chosen.length) return this.variants[0];
      return this.variants.find((v) => v.options.every((o, i) => chosen[i] === undefined || o === chosen[i]));
    }

    sync(fromPicker = false) {
      this.fieldsets.forEach((fs) => {
        const label = fs.querySelector('[data-chosen]');
        const checked = fs.querySelector('input:checked');
        if (label && checked) label.textContent = checked.value;
      });
      const v = this.current();
      const button = this.button;
      if (!v) {
        if (button) {
          button.disabled = true;
          button.textContent = button.dataset.labelUnavailable || 'Unavailable';
        }
        return;
      }
      const idInput = this.form?.querySelector('[data-variant-id]');
      if (idInput) idInput.value = v.id;

      const price = this.querySelector('[data-price]');
      if (price) price.textContent = money(v.price);
      const onSale = v.compare > v.price;
      this.querySelectorAll('[data-compare], [data-sale-label]').forEach((el) => (el.hidden = !onSale));
      const was = this.querySelector('[data-compare-amount]');
      if (was && onSale) was.textContent = money(v.compare);

      if (button) {
        button.disabled = !v.available;
        button.textContent = v.available ? button.dataset.labelAdd : button.dataset.labelSoldOut;
      }
      if (v.photo >= 0) this.showPhoto(v.photo);

      if (fromPicker && this.dataset.url) {
        const url = new URL(this.dataset.url, location.origin);
        url.search = location.search;
        url.searchParams.set('variant', v.id);
        history.replaceState(history.state, '', url.pathname + url.search);
      }
    }

    async submit(e) {
      e.preventDefault();
      const button = this.button;
      if (!button || button.disabled) return;
      const body = new FormData(this.form);
      for (const [key, value] of [...body.entries()]) {
        if (!key.startsWith('properties[')) continue;
        const clean = String(value).trim();
        if (clean) body.set(key, clean);
        else body.delete(key);
      }
      if (this.error) this.error.hidden = true;
      button.disabled = true;
      button.textContent = button.dataset.labelAdding || say('textAdding', 'Adding…');
      try {
        await add(body);
        button.textContent = button.dataset.labelAdded || 'Added';
        setTimeout(() => this.sync(), 1400);
      } catch (err) {
        button.textContent = button.dataset.labelRetry || 'Try Again';
        button.disabled = false;
        if (this.error) {
          this.error.textContent = err.message;
          this.error.hidden = false;
        }
      }
    }
  }
  if (!customElements.get('zl-product')) customElements.define('zl-product', ZlProduct);

  // The drawer element itself needs no behavior of its own; define it so it is a known element.
  if (!customElements.get('zl-cart-drawer')) customElements.define('zl-cart-drawer', class extends HTMLElement {});
})();
